import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PROTOCOL_STEPS } from '../../accidentProtocol';
import { apiFetch, getToken, API_BASE, photoUrl } from '../../services/apiClient';
import { empName, fmtDate } from '../../utils/ui';
import AttachButton, { AttachThumb, isPdf } from '../../components/AttachButton';
import * as XLSX from 'xlsx';
import PmFuelPanel from './PmFuelPanel';

// Who gets the Excel export on the fuel log (Pat, Sep 26 2026): the office and the Executives.
function canExportFuel() {
  try { return ['admin', 'Executive', 'Accounting'].includes(JSON.parse(localStorage.getItem('titan_session') || 'null')?.role); } catch { return false; }
}

const num = n => (n == null || n === '') ? '—' : Number(n).toLocaleString('en-US');

const SUBTABS = [
  { key: 'fuel',        label: 'Fuel',                 icon: '⛽', color: '#b45309', sub: 'Bulk-fuel log (IFTA)' },
  { key: 'inspections', label: 'Pre-Trip Inspections', icon: '📋', color: '#15803d', sub: 'Daily CDL DVIR' },
  { key: 'accident',    label: 'Accident Protocol',    icon: '🚨', color: '#b91c1c', sub: 'Incident reporting & steps' },
];

// Today on this computer's / phone's clock — toISOString() is UTC, so an evening fill-up read as tomorrow.
const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const EMPTY = { equipment_id: '', user_id: '', filled_at: localToday(), odometer: '', gallons: '', note: '', personal: false, ca_answer: '', ca_miles: '' };

function sessionUserId() {
  // The phone login (te_worker) or the desktop one (titan_session) — whichever this person used.
  try {
    const w = JSON.parse(localStorage.getItem('te_worker') || 'null');
    if (w && w.id) return w.id;
    const sess = JSON.parse(localStorage.getItem('titan_session') || 'null');
    return (sess && (sess.userId || (sess.user && sess.user.id))) || null;
  } catch { return null; }
}
function sessionName() {
  try {
    const w = JSON.parse(localStorage.getItem('te_worker') || 'null');
    return w ? `${w.firstName || ''} ${w.lastName || ''}`.trim().toLowerCase() : '';
  } catch { return ''; }
}

// Typable truck picker: an input you can type to filter, backed by a scrollable
// dropdown. `recentIds` (the driver's most recently fueled trucks) float to top.
function TruckCombo({ trucks, value, onChange, recentIds = [], style }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const label = t => `${t.asset_number ? t.asset_number + ' · ' : ''}${t.name}`;
  const selected = trucks.find(t => t.id === value);
  const ql = q.trim().toLowerCase();
  const matches = t => !ql || label(t).toLowerCase().includes(ql);
  const recent = recentIds.map(id => trucks.find(t => t.id === id)).filter(Boolean).filter(matches);
  const recentSet = new Set(recent.map(t => t.id));
  const rest = trucks.filter(t => !recentSet.has(t.id)).filter(matches);
  const pick = t => { onChange(t.id); setQ(''); setOpen(false); };
  const row = (t, badge) => (
    <div key={t.id} onMouseDown={e => { e.preventDefault(); pick(t); }}
      style={{ padding: '9px 11px', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, background: t.id === value ? '#eff6ff' : '#fff' }}>
      <span style={{ flex: 1 }}>{label(t)}</span>
      {badge && <span style={{ fontSize: 10, fontWeight: 700, color: '#b45309', background: '#fef3c7', borderRadius: 8, padding: '1px 7px' }}>{badge}</span>}
    </div>
  );
  const hd = txt => <div style={{ padding: '5px 11px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, background: '#f9fafb' }}>{txt}</div>;
  return (
    <div style={{ position: 'relative' }}>
      <input value={open ? q : (selected ? label(selected) : '')}
        onChange={e => { setQ(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setQ(''); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        placeholder="Type or select a truck…" style={style} />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, marginTop: 3, maxHeight: 240, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
          {recent.length > 0 && hd('Recently used')}
          {recent.map(t => row(t, 'recent'))}
          {rest.length > 0 && recent.length > 0 && hd('All trucks')}
          {rest.map(t => row(t, null))}
          {recent.length === 0 && rest.length === 0 && <div style={{ padding: '10px 11px', color: '#9ca3af', fontSize: 13 }}>No trucks match.</div>}
        </div>
      )}
    </div>
  );
}

function FuelLog({ restricted = false }) {
  const [logs, setLogs] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');   // "logged as PM fuel"

  function reload() { apiFetch('/fuel-logs').then(d => setLogs(Array.isArray(d) ? d : [])).catch(() => {}); }
  useEffect(() => {
    Promise.all([apiFetch('/fuel-logs').catch(() => []), apiFetch('/fuel-trucks').catch(() => []), apiFetch('/users').catch(() => [])])
      .then(([l, t, u]) => { setLogs(Array.isArray(l) ? l : []); setTrucks(Array.isArray(t) ? t : []); setUsers(Array.isArray(u) ? u : []); })
      .finally(() => setLoading(false));
  }, []);

  // Whoever is signed in, matched by id where the session carries one and by name
  // otherwise — the two sessions in this app don't store the same thing.
  const me = (() => {
    const id = sessionUserId();
    if (id && users.some(u => u.id === id)) return users.find(u => u.id === id);
    const nm = sessionName();
    return nm ? users.find(u => empName(u).trim().toLowerCase() === nm) : null;
  })();

  // The last truck this person actually fuelled. Falls back to the one assigned to
  // them, so a driver's first ever fill-up is prefilled too.
  function lastTruckFor(uid) {
    const mine = [...logs]
      .filter(l => l.equipment_id && l.user_id === uid)
      .sort((a, b) => (b.filled_at || '').localeCompare(a.filled_at || '')
        || (b.created_at || '').localeCompare(a.created_at || ''));
    if (mine[0]) return mine[0].equipment_id;
    const nm = me ? empName(me).trim().toLowerCase() : '';
    const assigned = nm && trucks.find(t => (t.assigned_to || '').trim().toLowerCase() === nm);
    return assigned ? assigned.id : '';
  }

  function startAdd() {
    const uid = me ? me.id : '';
    const truckId = uid ? lastTruckFor(uid) : '';
    const t = trucks.find(x => x.id === truckId);
    setEditId('new');
    // The odometer comes with the truck, the way it does when you pick one by hand.
    setForm({ ...EMPTY, filled_at: localToday(), user_id: uid, equipment_id: truckId, odometer: t && t.meter_reading != null ? t.meter_reading : '' });
    setErr('');
  }
  function startEdit(r) {
    setEditId(r.id);
    setForm({ equipment_id: r.equipment_id || '', user_id: r.user_id || '', filled_at: r.filled_at ? String(r.filled_at).slice(0, 10) : '', odometer: r.odometer ?? '', gallons: r.gallons ?? '', note: r.note || '',
      ca_answer: r.ca_miles == null ? '' : Number(r.ca_miles) > 0 ? 'yes' : 'no', ca_miles: r.ca_miles != null && Number(r.ca_miles) > 0 ? r.ca_miles : '' });
    setErr('');
  }
  function set(k, v) {
    setForm(f => {
      const next = { ...f, [k]: v };
      if (k === 'equipment_id' && editId === 'new') { const t = trucks.find(x => x.id === v); if (t && (next.odometer === '' || next.odometer == null)) next.odometer = t.meter_reading ?? ''; }
      return next;
    });
  }

  async function save() {
    // PM fuel: no truck, no odometer; it goes to the PM Fuel list, not this log (Pat, Oct 9 2026).
    if (form.personal) {
      if (!(Number(form.gallons) > 0)) { setErr('How many gallons?'); return; }
      setSaving(true); setErr('');
      try {
        await apiFetch('/fuel-logs', { method: 'POST', body: JSON.stringify({ personal: true, gallons: form.gallons, filled_at: form.filled_at, note: form.note }) });
        setEditId(null); setNotice(`✓ Logged ${form.gallons} gal as PM fuel — to be billed back.`); setTimeout(() => setNotice(''), 6000);
      } catch (e) { setErr(e?.error || e?.message || 'Save failed'); }
      setSaving(false);
      return;
    }
    if (!form.equipment_id) { setErr('Pick a truck'); return; }
    // A CDL truck or a CDL driver: California miles must be answered (IFTA; Pat, Oct 9 2026).
    if (cdlFill && !form.ca_answer) { setErr('Any miles in California? Answer Yes or No.'); return; }
    if (cdlFill && form.ca_answer === 'yes' && !(Number(form.ca_miles) > 0)) { setErr('How many miles in California?'); return; }
    setSaving(true); setErr('');
    try {
      const isNew = editId === 'new';
      const { ca_answer, ca_miles, personal, ...body } = form;
      const caMiles = cdlFill ? (ca_answer === 'yes' ? Number(ca_miles) : 0) : (ca_answer ? (ca_answer === 'yes' ? Number(ca_miles) : 0) : null);
      await apiFetch(isNew ? '/fuel-logs' : `/fuel-logs/${editId}`, { method: isNew ? 'POST' : 'PATCH', body: JSON.stringify({ ...body, ca_miles: caMiles }) });
      reload(); apiFetch('/fuel-trucks').then(t => setTrucks(Array.isArray(t) ? t : [])).catch(() => {});
      setEditId(null);
    } catch (e) { setErr(e.message || 'Save failed'); }
    setSaving(false);
  }
  async function del(id) { if (!window.confirm('Delete this fuel entry?')) return; try { await apiFetch(`/fuel-logs/${id}`, { method: 'DELETE' }); setLogs(prev => prev.filter(x => x.id !== id)); } catch {} }

  const truckLabel = t => `${t.asset_number ? t.asset_number + ' · ' : ''}${t.name}`;
  // The trucks the selected driver (or, if none picked, anyone) most recently
  // fueled — surfaced at the top of the truck picker.
  const recentTruckIds = (() => {
    const uid = form.user_id;
    const rel = [...logs]
      .filter(l => l.equipment_id && (!uid || l.user_id === uid))
      .sort((a, b) => (b.filled_at || '').localeCompare(a.filled_at || '') || (b.created_at || '').localeCompare(a.created_at || ''));
    const seen = [];
    for (const l of rel) { if (!seen.includes(l.equipment_id)) seen.push(l.equipment_id); if (seen.length >= 5) break; }
    return seen;
  })();
  const filtered = logs.filter(r => {
    const q = search.trim().toLowerCase();
    return !q || `${r.truck_name || ''} ${r.truck_asset || ''} ${r.driver_name || ''}`.toLowerCase().includes(q);
  });
  const totalGallons = logs.reduce((s, r) => s + (Number(r.gallons) || 0), 0);
  const formTruck = trucks.find(t => t.id === form.equipment_id);
  const formDriver = users.find(u => u.id === form.user_id);
  const cdlFill = !form.personal && ((formTruck && formTruck.category === 'CDL Trucks') || !!(formDriver && formDriver.isCdl));
  const inp = { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 9px', fontSize: 13, width: '100%', boxSizing: 'border-box' };
  const lblS = { fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>Bulk-fuel dispensing log (for IFTA). {logs.length} fill-up{logs.length !== 1 ? 's' : ''} · {totalGallons.toLocaleString('en-US')} gal total.</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search truck or driver…" style={{ ...inp, width: 220 }} />
          {!restricted && canExportFuel() && (
            // Every fill-up (the search is not applied), newest first, as a spreadsheet for IFTA.
            // Desktop only (Pat, Sep 27 2026): on the phone it crowded the header for no gain.
            <button onClick={() => {
              const rows = [...logs].sort((a, b) => (b.filled_at || '').localeCompare(a.filled_at || ''));
              const aoa = [['Date', 'Truck', 'Asset #', 'Driver', 'Odometer', 'Gallons', 'CA miles', 'Note'],
                ...rows.map(r => [r.filled_at ? String(r.filled_at).slice(0, 10) : '', r.truck_name || '', r.truck_asset || '', r.driver_name || '', r.odometer != null ? Number(r.odometer) : '', r.gallons != null ? Number(r.gallons) : '', r.ca_miles != null ? Number(r.ca_miles) : '', r.note || ''])];
              aoa.push([]); aoa.push(['', '', '', 'Total gallons', '', totalGallons, rows.reduce((n, r) => n + (Number(r.ca_miles) || 0), 0), '']);
              const ws = XLSX.utils.aoa_to_sheet(aoa);
              ws['!cols'] = [{ wch: 12 }, { wch: 28 }, { wch: 10 }, { wch: 22 }, { wch: 11 }, { wch: 9 }, { wch: 9 }, { wch: 36 }];
              const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Fuel Logs');
              const d = new Date(); const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              XLSX.writeFile(wb, `Fuel Logs ${local}.xlsx`);
            }} title="Download every fill-up as an Excel spreadsheet"
              style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #16a34a', background: '#f0fdf4', color: '#166534', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              ⬇ Export to Excel
            </button>
          )}
          <button className="admin-primary-btn" onClick={startAdd}>+ Add Fill-Up</button>
        </div>
      </div>
      {notice && <div style={{ margin: '0 0 10px', padding: '9px 14px', borderRadius: 9, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', fontWeight: 700, fontSize: 13 }}>{notice}</div>}

      <div className="admin-card" style={{ overflow: 'hidden' }}>
        {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
          : filtered.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>{logs.length === 0 ? 'No fuel logged yet.' : 'No matches.'}</div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead><tr><th>Date</th><th>Driver</th><th>Truck</th><th style={{ textAlign: 'right' }}>Odometer</th><th style={{ textAlign: 'right' }}>Gallons</th><th style={{ textAlign: 'right' }}>CA miles</th><th>Note</th><th style={{ width: 70 }}></th></tr></thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id}>
                      <td>{fmtDate(r.filled_at)}</td>
                      <td style={{ fontWeight: 600 }}>{r.driver_name || '—'}</td>
                      <td>🚚 {r.truck_asset ? `${r.truck_asset} · ` : ''}{r.truck_name || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{num(r.odometer)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{num(r.gallons)}</td>
                      <td style={{ textAlign: 'right', color: Number(r.ca_miles) > 0 ? '#b45309' : '#9ca3af', fontWeight: Number(r.ca_miles) > 0 ? 700 : 400 }}>{r.ca_miles == null ? '' : num(r.ca_miles)}</td>
                      <td className="admin-list-secondary">{r.note || ''}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button onClick={() => startEdit(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: 12, fontWeight: 600 }}>Edit</button>
                        {!restricted && <button onClick={() => del(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 15, marginLeft: 4 }}>×</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {editId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setEditId(null)}>
          <div style={{ background: '#fff', borderRadius: 14, width: 520, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 12px 48px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: '#b45309', borderRadius: '14px 14px 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>⛽ {editId === 'new' ? 'Add Fill-Up' : 'Edit Fill-Up'}</span>
              <button onClick={() => setEditId(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {editId === 'new' && me && ['ProjectManager', 'Executive'].includes(me.role) && (
                <label style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                  background: form.personal ? '#fff7ed' : '#f9fafb', border: `1.5px solid ${form.personal ? '#b45309' : '#e5e7eb'}` }}>
                  <input type="checkbox" checked={!!form.personal} onChange={e => set('personal', e.target.checked)} style={{ width: 18, height: 18, accentColor: '#b45309' }} />
                  <span><b style={{ color: '#92400e' }}>⛽ PM fuel — my own vehicle</b><br /><span style={{ fontSize: 12, color: '#6b7280' }}>To be billed back. No truck or mileage needed.</span></span>
                </label>
              )}
              {!form.personal && (<>
              <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 3 }}>
                <label style={lblS}>Truck *</label>
                <TruckCombo trucks={trucks} value={form.equipment_id} onChange={id => set('equipment_id', id)} recentIds={recentTruckIds} style={inp} />
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 3 }}>
                <label style={lblS}>Driver</label>
                <select value={form.user_id} onChange={e => set('user_id', e.target.value)} style={inp}>
                  <option value="">— Select driver —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{empName(u)}</option>)}
                </select>
              </div>
              </>)}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}><label style={lblS}>Date</label><input type="date" value={form.filled_at} onChange={e => set('filled_at', e.target.value)} style={inp} /></div>
              {!form.personal && <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}><label style={lblS}>Odometer (miles)</label><input type="number" value={form.odometer} onChange={e => set('odometer', e.target.value)} style={inp} /></div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}><label style={lblS}>Gallons</label><input type="number" step="0.1" value={form.gallons} onChange={e => set('gallons', e.target.value)} style={inp} /></div>
              {cdlFill && (
                <div style={{ gridColumn: '1 / -1', padding: '10px 12px', borderRadius: 10, background: '#fffbeb', border: '1.5px solid #f59e0b', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: '#92400e' }}>🚛 CDL — any miles in California on this tank?</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[['no', 'No'], ['yes', 'Yes']].map(([k, l]) => (
                      <button key={k} type="button" onClick={() => { set('ca_answer', k); if (k === 'no') set('ca_miles', ''); }}
                        style={{ padding: '7px 18px', borderRadius: 8, border: `1.5px solid ${form.ca_answer === k ? '#b45309' : '#e5e7eb'}`, background: form.ca_answer === k ? '#b45309' : '#fff', color: form.ca_answer === k ? '#fff' : '#374151', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>{l}</button>
                    ))}
                  </div>
                  {form.ca_answer === 'yes' && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}><span style={lblS}>How many miles in California?</span>
                      <input type="number" inputMode="decimal" min="0" value={form.ca_miles} onChange={e => set('ca_miles', e.target.value)} style={{ ...inp, maxWidth: 200 }} autoFocus /></label>
                  )}
                </div>
              )}
              <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 3 }}><label style={lblS}>Note</label><input value={form.note} onChange={e => set('note', e.target.value)} style={inp} /></div>
              {err && <div style={{ gridColumn: '1 / -1', color: '#dc2626', fontSize: 13 }}>{err}</div>}
              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button onClick={() => setEditId(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button onClick={save} disabled={saving} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PretripLog({ restricted = false }) {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [trucks, setTrucks] = useState([]); // CDL trucks
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(''); // '' = all, or 'id:<equipmentId>'
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    apiFetch('/pretrip-reports').then(d => setReports(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setLoading(false));
    apiFetch('/pretrip-trucks').then(d => setTrucks(Array.isArray(d) ? d : [])).catch(() => {});
    // A notification pointed at one inspection — open it straight away by id.
    const rid = sp.get('report');
    if (rid) open({ id: rid });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function open(r) { try { setDetail(await apiFetch(`/pretrip-reports/${r.id}`)); } catch {} }
  async function del(id) { if (!window.confirm('Delete this inspection report?')) return; try { await apiFetch(`/pretrip-reports/${id}`, { method: 'DELETE' }); setReports(p => p.filter(x => x.id !== id)); setDetail(null); } catch {} }

  const truckLabel = t => `${t.asset_number ? t.asset_number + ' · ' : ''}${t.name}`;
  const filtered = search.startsWith('id:') ? reports.filter(r => r.equipment_id === search.slice(3)) : reports;
  const defectsToday = reports.filter(r => r.overall === 'defect').length;

  const checklist = detail ? (() => { try { return JSON.parse(detail.checklist || '[]'); } catch { return []; } })() : [];
  const sections = [...new Set(checklist.map(i => i.section))];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>Daily CDL pre-trip inspections (DVIR). {reports.length} report{reports.length !== 1 ? 's' : ''}{defectsToday > 0 ? ` · ${defectsToday} with defects` : ''}.</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={search} onChange={e => setSearch(e.target.value)} style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 9px', fontSize: 13, width: 200 }}>
            <option value="">All CDL trucks</option>
            {trucks.map(t => <option key={t.id} value={`id:${t.id}`}>{truckLabel(t)}</option>)}
          </select>
          <button onClick={() => navigate('/pretrip')} className="admin-primary-btn" style={{ background: '#15803d', whiteSpace: 'nowrap' }}>+ New Inspection</button>
        </div>
      </div>

      <div className="admin-card" style={{ overflow: 'hidden' }}>
        {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
          : filtered.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>{reports.length === 0 ? 'No inspections submitted yet.' : 'No matches.'}</div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead><tr><th>Date</th><th>Truck</th><th>Driver</th><th>Result</th><th>Certified</th><th style={{ width: 90 }}></th></tr></thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => open(r)}>
                      <td>{fmtDate(r.report_date)}</td>
                      <td>🚚 {r.truck_asset ? `${r.truck_asset} · ` : ''}{r.truck_name || '—'}</td>
                      <td style={{ fontWeight: 600 }}>{r.driver_name || '—'}</td>
                      <td>{r.overall === 'defect'
                        ? <span style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c', background: '#fef2f2', padding: '2px 8px', borderRadius: 10 }}>DEFECTS ({r.defect_count})</span>
                        : <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: '#f0fdf4', padding: '2px 8px', borderRadius: 10 }}>PASS</span>}</td>
                      <td className="admin-list-secondary">{r.certified ? `✓ ${r.certified_name || ''}` : '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        <button onClick={() => open(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: 12, fontWeight: 600 }}>View</button>
                        {!restricted && <button onClick={() => del(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 15, marginLeft: 4 }}>×</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {detail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setDetail(null)}>
          <div style={{ background: '#fff', borderRadius: 14, width: 640, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 12px 48px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: detail.overall === 'defect' ? '#b91c1c' : '#15803d', borderRadius: '14px 14px 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>🚚 {detail.truck_name}</div>
                <div style={{ fontSize: 12, opacity: 0.9 }}>{detail.driver_name} · {fmtDate(detail.report_date)} · {detail.overall === 'defect' ? `${detail.defect_count} defect(s)` : 'Pass'}</div>
              </div>
              <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              {sections.map(sec => (
                <div key={sec} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{sec}</div>
                  {checklist.filter(i => i.section === sec).map(i => (
                    <div key={i.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '5px 0', borderBottom: '1px solid #f3f4f6' }}>
                      <div style={{ fontSize: 13, color: '#374151' }}>{i.label}{i.status === 'defect' && i.note ? <span style={{ color: '#b91c1c', fontSize: 12 }}> — {i.note}</span> : ''}</div>
                      {i.status === 'defect'
                        ? <span style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c', background: '#fef2f2', padding: '1px 8px', borderRadius: 8, whiteSpace: 'nowrap' }}>DEFECT</span>
                        : <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', whiteSpace: 'nowrap' }}>OK</span>}
                    </div>
                  ))}
                </div>
              ))}
              {detail.note && <div style={{ fontSize: 13, color: '#374151', marginBottom: 14 }}><strong>Notes:</strong> {detail.note}</div>}
              {detail.photos && detail.photos.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Photos</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {detail.photos.map(p => (
                      <a key={p.id} href={photoUrl(p.filename)} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                        <img src={photoUrl(p.filename)} alt={p.label || ''} style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                        <div style={{ fontSize: 10, color: '#6b7280', textAlign: 'center', marginTop: 2 }}>{p.label || ''}</div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ fontSize: 12, color: '#6b7280', borderTop: '1px solid #f3f4f6', paddingTop: 10 }}>
                {detail.certified ? `Certified by ${detail.certified_name}` : 'Not certified'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ACC_STATUS = {
  open:   { label: 'Open',         color: '#b91c1c', bg: '#fef2f2' },
  review: { label: 'Under Review', color: '#b45309', bg: '#fffbeb' },
  closed: { label: 'Closed',       color: '#15803d', bg: '#f0fdf4' },
};
const EMPTY_EVENT = {
  equipment_id: '', user_id: '', occurred_at: new Date().toISOString().slice(0, 16), location: '', conditions: '', description: '',
  injuries: 0, injury_details: '', police_responded: 0, police_agency: '', officer: '', report_number: '', citations: '',
  other_party: '', other_phone: '', other_license: '', other_insurance: '', other_vehicle: '', witnesses: '',
  our_damage: '', other_damage: '', towed_by: '', cargo_spill: 0, dot_recordable: 0, post_accident_test: 0, claim_number: '', status: 'open',
};

function AccidentProtocol({ restricted = false }) {
  const [sp] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProtocol, setShowProtocol] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [photos, setPhotos] = useState([]);
  const [pending, setPending] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function reload() { apiFetch('/accident-events').then(d => setEvents(Array.isArray(d) ? d : [])).catch(() => {}); }
  useEffect(() => {
    Promise.all([apiFetch('/accident-events').catch(() => []), apiFetch('/fuel-trucks').catch(() => []), apiFetch('/users').catch(() => [])])
      .then(([e, t, u]) => { setEvents(Array.isArray(e) ? e : []); setTrucks(Array.isArray(t) ? t : []); setUsers(Array.isArray(u) ? u : []); })
      .finally(() => setLoading(false));
    // A notification pointed at one accident — open its record straight away.
    const eid = sp.get('event');
    if (eid) open({ id: eid });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function startNew() { setForm(EMPTY_EVENT); setPhotos([]); setPending([]); setErr(''); setEditId('new'); }
  async function open(ev) {
    setErr(''); setPending([]);
    try {
      const full = await apiFetch(`/accident-events/${ev.id}`);
      const next = { ...EMPTY_EVENT };
      Object.keys(EMPTY_EVENT).forEach(k => { next[k] = full[k] ?? EMPTY_EVENT[k]; });
      next.occurred_at = full.occurred_at ? String(full.occurred_at).slice(0, 16) : '';
      setForm(next); setPhotos(full.photos || []); setEditId(ev.id);
    } catch {}
  }

  async function save() {
    setSaving(true); setErr('');
    try {
      const isNew = editId === 'new';
      const saved = await apiFetch(isNew ? '/accident-events' : `/accident-events/${editId}`, { method: isNew ? 'POST' : 'PATCH', body: JSON.stringify(form) });
      for (const file of pending) {
        const fd = new FormData(); fd.append('file', file);
        await fetch(`${API_BASE}/accident-events/${saved.id}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      }
      reload(); setEditId(null);
    } catch (e) { setErr(e.message || 'Save failed'); }
    setSaving(false);
  }
  async function del(id) { if (!window.confirm('Delete this accident event?')) return; try { await apiFetch(`/accident-events/${id}`, { method: 'DELETE' }); setEvents(p => p.filter(x => x.id !== id)); setEditId(null); } catch {} }
  async function delPhoto(pid) { try { await apiFetch(`/accident-events/${editId}/photos/${pid}`, { method: 'DELETE' }); setPhotos(p => p.filter(x => x.id !== pid)); } catch {} }

  const truckLabel = t => `${t.asset_number ? t.asset_number + ' · ' : ''}${t.name}`;
  const inp = { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 9px', fontSize: 13, width: '100%', boxSizing: 'border-box' };
  const lblS = { fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' };
  const secHdr = { fontSize: 11, fontWeight: 700, color: '#7f1d1d', textTransform: 'uppercase', letterSpacing: 0.4, gridColumn: '1 / -1', marginTop: 6, borderTop: '1px solid #f3f4f6', paddingTop: 10 };
  const field = (label, k, opts = {}) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, gridColumn: opts.full ? '1 / -1' : 'auto' }}>
      <label style={lblS}>{label}</label>
      {opts.textarea
        ? <textarea value={form[k]} onChange={e => set(k, e.target.value)} rows={opts.rows || 2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
        : <input type={opts.type || 'text'} value={form[k]} onChange={e => set(k, e.target.value)} style={inp} />}
    </div>
  );
  const check = (label, k) => (
    <label style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#374151', fontWeight: 600 }}>
      <input type="checkbox" checked={!!Number(form[k])} onChange={e => set(k, e.target.checked ? 1 : 0)} /> {label}
    </label>
  );

  return (
    <div>
      {/* Top-left action */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <button className="admin-primary-btn" style={{ background: '#b91c1c' }} onClick={startNew}>+ Create New Event</button>
        <div style={{ fontSize: 13, color: '#6b7280' }}>{events.length} event{events.length !== 1 ? 's' : ''}{events.filter(e => e.status === 'open').length ? ` · ${events.filter(e => e.status === 'open').length} open` : ''}</div>
      </div>

      {/* Protocol reference */}
      <div className="admin-card" style={{ overflow: 'hidden', marginBottom: 16 }}>
        <div onClick={() => setShowProtocol(v => !v)} style={{ background: '#7f1d1d', color: '#fff', padding: '10px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.6 }}>🚨 On-Scene Protocol</span>
          <span style={{ fontSize: 11, opacity: 0.8 }}>{showProtocol ? '▲' : '▼'}</span>
        </div>
        {showProtocol && (
          <ol style={{ margin: 0, padding: '14px 20px 14px 36px', color: '#374151', fontSize: 13, lineHeight: 1.7 }}>
            {PROTOCOL_STEPS.map((s, i) => <li key={i} style={{ marginBottom: 4 }}>{s}</li>)}
          </ol>
        )}
      </div>

      {/* Events list */}
      <div className="admin-card" style={{ overflow: 'hidden' }}>
        {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
          : events.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No accident events logged.</div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead><tr><th>Date</th><th>Driver</th><th>Truck</th><th>Location</th><th>Injuries</th><th>Status</th><th style={{ width: 70 }}></th></tr></thead>
                <tbody>
                  {events.map(ev => { const sm = ACC_STATUS[ev.status] || ACC_STATUS.open; return (
                    <tr key={ev.id} style={{ cursor: 'pointer' }} onClick={() => open(ev)}>
                      <td>{fmtDate(ev.occurred_at)}</td>
                      <td style={{ fontWeight: 600 }}>{ev.driver_name || '—'}</td>
                      <td>{ev.truck_name ? `🚚 ${ev.truck_name}` : '—'}</td>
                      <td className="admin-list-secondary">{ev.location || '—'}</td>
                      <td>{Number(ev.injuries) ? <span style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c' }}>⚠ Yes</span> : <span className="admin-list-secondary">No</span>}</td>
                      <td><span style={{ fontSize: 11, fontWeight: 700, color: sm.color, background: sm.bg, padding: '2px 8px', borderRadius: 10 }}>{sm.label}</span></td>
                      <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                        <button onClick={() => open(ev)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: 12, fontWeight: 600 }}>Open</button>
                        {!restricted && <button onClick={() => del(ev.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 15, marginLeft: 4 }}>×</button>}
                      </td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {/* Event form modal */}
      {editId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setEditId(null)}>
          <div style={{ background: '#fff', borderRadius: 14, width: 680, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 12px 48px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: '#b91c1c', borderRadius: '14px 14px 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>🚨 {editId === 'new' ? 'New Accident Event' : 'Accident Event'}</span>
              <button onClick={() => setEditId(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={secHdr}>Scene</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}><label style={lblS}>Date &amp; Time</label><input type="datetime-local" value={form.occurred_at} onChange={e => set('occurred_at', e.target.value)} style={inp} /></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}><label style={lblS}>Status</label><select value={form.status} onChange={e => set('status', e.target.value)} style={inp}><option value="open">Open</option><option value="review">Under Review</option><option value="closed">Closed</option></select></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}><label style={lblS}>Truck</label><select value={form.equipment_id} onChange={e => set('equipment_id', e.target.value)} style={inp}><option value="">— Select —</option>{trucks.map(t => <option key={t.id} value={t.id}>{truckLabel(t)}</option>)}</select></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}><label style={lblS}>Driver</label><select value={form.user_id} onChange={e => set('user_id', e.target.value)} style={inp}><option value="">— Select —</option>{users.map(u => <option key={u.id} value={u.id}>{empName(u)}</option>)}</select></div>
              {field('Location', 'location', { full: true })}
              {field('Weather / road conditions', 'conditions', { full: true })}
              {field('What happened', 'description', { full: true, textarea: true, rows: 3 })}

              <div style={secHdr}>Injuries</div>
              {check('Anyone injured / transported', 'injuries')}
              {form.injuries ? field('Injury details', 'injury_details', { full: true, textarea: true }) : null}

              <div style={secHdr}>Police</div>
              {check('Police responded', 'police_responded')}
              {field('Agency', 'police_agency')}
              {field('Officer name / badge', 'officer')}
              {field('Report number', 'report_number')}
              {field('Citations issued (to whom)', 'citations')}

              <div style={secHdr}>Other Party</div>
              {field('Name', 'other_party')}
              {field('Phone', 'other_phone', { type: 'tel' })}
              {field('Driver license #', 'other_license')}
              {field('Insurance co. & policy #', 'other_insurance')}
              {field('Vehicle (year/make/model) & plate', 'other_vehicle', { full: true })}
              {field('Witnesses (name + phone)', 'witnesses', { full: true, textarea: true })}

              <div style={secHdr}>Damage</div>
              {field('Our truck damage', 'our_damage', { full: true, textarea: true })}
              {field('Other property damage', 'other_damage', { full: true, textarea: true })}
              {field('Towed by', 'towed_by')}
              {check('Cargo / fluid spill involved', 'cargo_spill')}

              <div style={secHdr}>Follow-Up &amp; Compliance</div>
              {check('DOT recordable (fatality, injury w/ treatment, or tow-away)', 'dot_recordable')}
              {check('Post-accident drug/alcohol test done', 'post_accident_test')}
              {field('Insurance claim #', 'claim_number')}

              <div style={secHdr}>Photos &amp; files</div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  {photos.map(p => (
                    <AttachThumb key={p.id} size={80} src={photoUrl(p.filename)} name={p.original_name || p.filename} href={photoUrl(p.filename)} onRemove={() => delPhoto(p.id)} />
                  ))}
                  {pending.map((f, i) => <AttachThumb key={i} size={80} src={isPdf(f) ? null : URL.createObjectURL(f)} name={f.name} onRemove={() => setPending(p => p.filter((_, j) => j !== i))} />)}
                </div>
                <AttachButton multiple onFiles={fs => setPending(p => [...p, ...fs])} />
              </div>

              {err && <div style={{ gridColumn: '1 / -1', color: '#dc2626', fontSize: 13 }}>{err}</div>}
              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', gap: 10, paddingTop: 6 }}>
                {editId !== 'new' && !restricted ? <button onClick={() => del(editId)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff', color: '#dc2626', fontSize: 13, cursor: 'pointer' }}>Delete</button> : <span />}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setEditId(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={save} disabled={saving} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save Event'}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function DriverInfoContent({ restricted = false }) {
  const [sp] = useSearchParams();
  // A notification (accident, pre-trip defect) names the sub-tab to land on; the leaf
  // then opens the exact record from ?event= / ?report=.
  const wantSub = sp.get('sub');
  // PM Fuel (PMs' own vehicles, to be billed back) — the office and the Executives only (Pat, Oct 9 2026).
  const subtabs = canExportFuel() && !restricted ? [...SUBTABS, { key: 'pmfuel', label: 'PM Fuel', icon: '🧾', color: '#92400e', sub: 'PMs\u2019 own vehicles · to be billed' }] : SUBTABS;
  const [tab, setTab] = useState(subtabs.some(t => t.key === wantSub) ? wantSub : null);
  const active = subtabs.find(t => t.key === tab);

  if (!active) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
        {subtabs.map(c => (
          <button key={c.key} onClick={() => setTab(c.key)}
            style={{ textAlign: 'left', background: '#fff', border: '1px solid var(--border)', borderTop: `4px solid ${c.color}`, borderRadius: 12, padding: 20, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8, transition: 'box-shadow .15s, transform .1s' }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
            <div style={{ fontSize: 30 }}>{c.icon}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{c.label}</div>
            <div style={{ fontSize: 13, color: c.color, fontWeight: 600 }}>{c.sub}</div>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => setTab(null)}
        style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
        ‹ Driver Tools
      </button>
      {tab === 'fuel' && <FuelLog restricted={restricted} />}
      {tab === 'inspections' && <PretripLog restricted={restricted} />}
      {tab === 'accident' && <AccidentProtocol restricted={restricted} />}
      {tab === 'pmfuel' && <PmFuelPanel />}
    </div>
  );
}
